import React, { useState, useRef, useCallback } from 'react';
import { bulkApi } from '../../services/apiService';
import './BulkImportExportModal.css';

const BulkImportExportModal = ({ isOpen, onClose, entityType, onImportSuccess, isAdmin = false }) => {
  const [activeTab, setActiveTab] = useState('import'); // 'import' or 'export'
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const fileInputRef = useRef(null);

  const entityLabel = entityType === 'products' ? 'Products' : entityType === 'suppliers' ? 'Suppliers' : 'Projects';

  const resetState = useCallback(() => {
    setFile(null);
    setPreviewData(null);
    setImportResult(null);
    setError('');
    setLoading(false);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(ext)) {
      setError('Invalid file type. Please upload a CSV or Excel (.xlsx) file.');
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit.');
      return;
    }
    setFile(selectedFile);
    setError('');
    setPreviewData(null);
    setImportResult(null);
  };

  // Preview file
  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      let result;
      if (entityType === 'products') result = await bulkApi.previewProducts(file);
      else if (entityType === 'suppliers') result = await bulkApi.previewSuppliers(file);
      else result = await bulkApi.previewProjects(file);
      setPreviewData(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to preview file');
    } finally {
      setLoading(false);
    }
  };

  // Import file
  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      let result;
      if (entityType === 'products') result = await bulkApi.importProducts(file);
      else if (entityType === 'suppliers') result = await bulkApi.importSuppliers(file);
      else result = await bulkApi.importProjects(file);
      setImportResult(result);
      if (result.imported > 0 && onImportSuccess) {
        onImportSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  // Export data
  const handleExport = async () => {
    setLoading(true);
    setError('');
    try {
      let response;
      if (entityType === 'products') response = await bulkApi.exportProducts(exportFormat);
      else if (entityType === 'suppliers') response = await bulkApi.exportSuppliers(exportFormat);
      else response = await bulkApi.exportProjects(exportFormat);

      // Check if response.data is actually an error (JSON blob)
      if (response.data && response.data.type && response.data.type.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        setError(errorData.error || 'Export failed');
        return;
      }

      const mimeType = exportFormat === 'csv'
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entityType}_export.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Delay revoking to ensure download starts
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      // When responseType is 'blob', error response data is also a blob
      let errorMsg = 'Export failed';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const errorData = JSON.parse(text);
          errorMsg = errorData.error || errorMsg;
        } catch (_) {}
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Download template
  const handleDownloadTemplate = async (format) => {
    try {
      const response = await bulkApi.getTemplate(entityType, format);
      const mimeType = format === 'csv'
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entityType}_template.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError('Failed to download template');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bie-overlay" onClick={handleClose}>
      <div className="bie-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bie-header">
          <h2 className="bie-title">Bulk {entityLabel} Management</h2>
          <button className="bie-close" onClick={handleClose}>&times;</button>
        </div>

        {/* Tabs */}
        <div className="bie-tabs">
          <button
            className={`bie-tab ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => { setActiveTab('import'); resetState(); }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Import
          </button>
          <button
            className={`bie-tab ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => { setActiveTab('export'); resetState(); }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export
          </button>
        </div>

        <div className="bie-body">
          {/* IMPORT TAB */}
          {activeTab === 'import' && (
            <div className="bie-import-section">
              {/* Template Download */}
              <div className="bie-template-bar">
                <span>Download template to see required format:</span>
                <div className="bie-template-btns">
                  <button className="bie-template-btn" onClick={() => handleDownloadTemplate('xlsx')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    XLSX Template
                  </button>
                  <button className="bie-template-btn" onClick={() => handleDownloadTemplate('csv')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    CSV Template
                  </button>
                </div>
              </div>

              {/* File Upload Zone */}
              {!importResult && (
                <div
                  className={`bie-dropzone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileInput}
                    hidden
                  />
                  {file ? (
                    <div className="bie-file-info">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1F396C" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <div>
                        <p className="bie-file-name">{file.name}</p>
                        <p className="bie-file-size">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button className="bie-remove-file" onClick={(e) => { e.stopPropagation(); setFile(null); setPreviewData(null); }}>
                        &times;
                      </button>
                    </div>
                  ) : (
                    <div className="bie-drop-content">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <p>Drag & drop your file here, or <span className="bie-browse">browse</span></p>
                      <p className="bie-hint">Supports CSV and Excel (.xlsx) files up to 10MB</p>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              {file && !importResult && (
                <div className="bie-actions">
                  <button className="bie-btn bie-btn-secondary" onClick={handlePreview} disabled={loading}>
                    {loading && !previewData ? 'Parsing...' : 'Preview Data'}
                  </button>
                  <button className="bie-btn bie-btn-primary" onClick={handleImport} disabled={loading}>
                    {loading && previewData ? 'Importing...' : 'Import Now'}
                  </button>
                </div>
              )}

              {/* Preview Table */}
              {previewData && !importResult && (
                <div className="bie-preview">
                  <div className="bie-preview-summary">
                    <div className="bie-stat bie-stat-total">
                      <span className="bie-stat-number">{previewData.totalRows}</span>
                      <span className="bie-stat-label">Total Rows</span>
                    </div>
                    <div className="bie-stat bie-stat-valid">
                      <span className="bie-stat-number">{previewData.validCount}</span>
                      <span className="bie-stat-label">Valid</span>
                    </div>
                    <div className="bie-stat bie-stat-error">
                      <span className="bie-stat-number">{previewData.errorCount}</span>
                      <span className="bie-stat-label">Errors</span>
                    </div>
                  </div>

                  {/* Valid rows preview */}
                  {previewData.validRows.length > 0 && (
                    <div className="bie-preview-table-wrap">
                      <h4>Valid Rows (first 10)</h4>
                      <div className="bie-table-scroll">
                        <table className="bie-table">
                          <thead>
                            <tr>
                              <th>Row</th>
                              {Object.keys(previewData.validRows[0].data).map(key => (
                                <th key={key}>{key}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.validRows.slice(0, 10).map((item, i) => (
                              <tr key={i}>
                                <td>{item.row}</td>
                                {Object.values(item.data).map((val, j) => (
                                  <td key={j}>{String(val).substring(0, 50)}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Error rows */}
                  {previewData.errorRows.length > 0 && (
                    <div className="bie-error-list">
                      <h4>Rows with Errors</h4>
                      {previewData.errorRows.map((item, i) => (
                        <div key={i} className="bie-error-row">
                          <span className="bie-error-row-num">Row {item.row}</span>
                          {item.errors.map((err, j) => (
                            <span key={j} className="bie-error-msg">{err}</span>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Import Result */}
              {importResult && (
                <div className="bie-result">
                  <div className={`bie-result-banner ${importResult.imported > 0 ? 'success' : 'warning'}`}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {importResult.imported > 0 ? (
                        <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>
                      ) : (
                        <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>
                      )}
                    </svg>
                    <div>
                      <p className="bie-result-title">{importResult.message}</p>
                      <p className="bie-result-detail">
                        {importResult.imported} imported, {importResult.skipped} skipped
                      </p>
                    </div>
                  </div>

                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="bie-error-list">
                      <h4>Error Details</h4>
                      {importResult.errors.slice(0, 20).map((item, i) => (
                        <div key={i} className="bie-error-row">
                          <span className="bie-error-row-num">Row {item.row}</span>
                          {item.errors.map((err, j) => (
                            <span key={j} className="bie-error-msg">{err}</span>
                          ))}
                        </div>
                      ))}
                      {importResult.errors.length > 20 && (
                        <p className="bie-more-errors">...and {importResult.errors.length - 20} more errors</p>
                      )}
                    </div>
                  )}

                  <div className="bie-actions">
                    <button className="bie-btn bie-btn-secondary" onClick={() => { resetState(); }}>
                      Import Another File
                    </button>
                    <button className="bie-btn bie-btn-primary" onClick={handleClose}>
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EXPORT TAB */}
          {activeTab === 'export' && (
            <div className="bie-export-section">
              <div className="bie-export-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1F396C" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </div>
              <p className="bie-export-desc">
                Export all {entityLabel.toLowerCase()} data from the database in your preferred format.
              </p>

              <div className="bie-format-select">
                <label className="bie-format-label">Export Format:</label>
                <div className="bie-format-options">
                  <label className={`bie-format-option ${exportFormat === 'xlsx' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="xlsx"
                      checked={exportFormat === 'xlsx'}
                      onChange={(e) => setExportFormat(e.target.value)}
                    />
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    Excel (.xlsx)
                  </label>
                  <label className={`bie-format-option ${exportFormat === 'csv' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={(e) => setExportFormat(e.target.value)}
                    />
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    CSV (.csv)
                  </label>
                </div>
              </div>

              <button className="bie-btn bie-btn-primary bie-export-btn" onClick={handleExport} disabled={loading}>
                {loading ? (
                  <>
                    <span className="bie-spinner"></span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download {entityLabel}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bie-error-banner">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportExportModal;
